import { cx, useSearchInput } from "@jugl-web/utils";
import {
  ForwardedRef,
  Fragment,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { HorizontalScrollableContainer } from "@jugl-web/ui-components/web/common/HorizontalScrollableContainer";
import { SearchInput } from "../SearchInput";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import animationData from "./assets/loading-animation.json";
import { Divider } from "./components/Divider";
import { EmptyState } from "./components/EmptyState";
import {
  ListBoxItemComponent,
  ListBoxItemComponentProps,
} from "./components/ListBoxItemComponent";

export interface ListBoxItem<TValue> {
  id: string;
  value: TValue;
  isDisabled?: boolean;
}

export interface UnsafeListBoxItem<TValue> {
  id: string;
  value?: TValue;
}

export interface ListBoxHandle<TValue> {
  getSelectedIds: () => string[];
  getSelectedItems: () => UnsafeListBoxItem<TValue>[];
  clearSelection: () => void;
}

export type ListBoxSelectionBehavior =
  | { mode: "single"; canToggle: boolean }
  | { mode: "multiple" }
  | { mode: "readonly" };

export type ListBoxLoading = "skeleton" | "bottom-spinner";

export type ListBoxItemSize = "sm" | "md" | "lg" | "xl" | "2xl";
export type ListBoxSpaceBetweenItems = "normal" | "compact";

type RenderItemDataFn<TItemValue, TReturnType> = (
  item: ListBoxItem<TItemValue>,
  index: number,
  isSelected: boolean
) => TReturnType;

export interface ListBoxAddButtonOptions {
  label: ReactNode;
  startIcon?: ReactNode;
  onClick: () => void;
}

export interface ListBoxProps<TValue> {
  items: ListBoxItem<TValue>[];
  selectionBehavior: ListBoxSelectionBehavior;
  loading?: ListBoxLoading;
  isCompact?: boolean;
  hasSearch?: boolean;
  hasSearchAutoFocus?: boolean;
  emptyStateMessage?: string;
  noSearchResultsMessage?: string;
  isHeightFixed?: boolean;
  customListHeight?: number;
  shouldFilterOnSearch?: boolean;
  shouldScrollToFirstSelectedItem?: boolean;
  defaultSelectedIds?: string[];
  maxVisibleItems?: number;
  itemSize?: ListBoxItemSize;
  spaceBetweenItems?: ListBoxSpaceBetweenItems;
  addButton?: ListBoxAddButtonOptions;
  className?: string;
  buttonType?: "checkmark" | "checkbox";
  extraTopContent?: ReactNode;
  renderLabel?: RenderItemDataFn<TValue, ReactNode>;
  renderSearchLabel?: RenderItemDataFn<TValue, string>;
  renderStartIcon?: RenderItemDataFn<TValue, ReactNode>;
  renderEndIcon?: RenderItemDataFn<TValue, ReactNode>;
  renderSecondaryText?: RenderItemDataFn<TValue, ReactNode>;
  renderCustomItem?: (
    item: ListBoxItem<TValue>,
    index: number,
    props: ListBoxItemComponentProps
  ) => ReactNode;
  renderEmptyState?: (hasSearchQuery: boolean) => ReactNode;
  onSelectionChange?: (
    ids: string[],
    items: UnsafeListBoxItem<TValue>[]
  ) => void;
  onSelect?: (item: ListBoxItem<TValue>, isSelected: boolean) => void;
  onSearch?: (searchQuery: string) => void;
  onReachEnd?: () => void;

  filters?: { title: string; value: string }[];
  selectedFilter?: string;
  onFilterChange?: (filter: string) => void;
}

const itemSizeToHeight: Record<ListBoxItemSize, number> = {
  sm: 36,
  md: 40,
  lg: 44,
  xl: 48,
  "2xl": 72,
};

const spaceBetweenItemsToGap: Record<ListBoxSpaceBetweenItems, number> = {
  normal: 16,
  compact: 6,
};

const ListBoxInner = <TValue,>(
  {
    items,
    selectionBehavior,
    loading,
    isCompact = false,
    hasSearch = false,
    hasSearchAutoFocus = true,
    emptyStateMessage,
    noSearchResultsMessage,
    shouldFilterOnSearch = true,
    shouldScrollToFirstSelectedItem = false,
    defaultSelectedIds = [],
    maxVisibleItems,
    itemSize,
    spaceBetweenItems,
    addButton,
    isHeightFixed,
    className,
    buttonType = "checkmark",
    extraTopContent,
    renderLabel,
    renderSearchLabel,
    renderStartIcon,
    renderEndIcon,
    renderSecondaryText,
    renderCustomItem,
    onSelectionChange,
    onSelect,
    onSearch,
    onReachEnd,
    filters,
    onFilterChange,
    selectedFilter,
  }: ListBoxProps<TValue>,
  ref: ForwardedRef<ListBoxHandle<TValue>>
) => {
  const [selectedItemsToValues, setSelectedItemsToValues] = useState<
    Record<string, TValue | undefined>
  >(() => Object.fromEntries(defaultSelectedIds.map((id) => [id, undefined])));
  const listRef = useRef<HTMLDivElement | null>(null);

  const { ref: inViewRef } = useInView({
    root: listRef.current,
    onChange: (inView) => {
      if (inView) {
        onReachEnd?.();
      }
    },
  });

  const { hasSearchQuery, searchQuery, reset, inputProps } = useSearchInput({
    onSearch,
  });

  const itemById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );

  const transformIdsToItems = (ids: string[]): UnsafeListBoxItem<TValue>[] =>
    ids.map((id) => {
      // First, try to get the item directly from the items passed as a prop
      const item = itemById.get(id);

      if (item) {
        return item;
      }

      // If the item is not present anymore, try to get it from the selectedItemsToValues map,
      // in case it was selected and cached before
      const cachedItem = selectedItemsToValues[id];

      if (cachedItem) {
        return { id, value: cachedItem };
      }

      // If the item is still not found, return unsafe item with no value
      return { id };
    });

  useImperativeHandle(ref, () => ({
    getSelectedIds: () => Object.keys(selectedItemsToValues),
    getSelectedItems: () =>
      transformIdsToItems(Object.keys(selectedItemsToValues)),
    clearSelection: () => {
      setSelectedItemsToValues({});
      onSelectionChange?.([], []);
    },
  }));

  const isItemSelected = useCallback(
    (id: string) => id in selectedItemsToValues,
    [selectedItemsToValues]
  );

  const visibleItems = useMemo(() => {
    if (!shouldFilterOnSearch || !hasSearchQuery) {
      return items;
    }

    return items.filter((item, index) => {
      const searchLabel =
        renderSearchLabel?.(item, index, isItemSelected(item.id)) ||
        renderLabel?.(item, index, isItemSelected(item.id));

      if (typeof searchLabel === "string") {
        return searchLabel.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    });
  }, [
    shouldFilterOnSearch,
    hasSearchQuery,
    items,
    renderSearchLabel,
    isItemSelected,
    renderLabel,
    searchQuery,
  ]);

  const isLoading = !!loading;
  const isEmpty = visibleItems.length === 0;

  const handleSelect = (selectedItem: ListBoxItem<TValue>) => {
    if (selectionBehavior.mode === "readonly") {
      onSelect?.(selectedItem, false);
      return;
    }

    let updatedSelectedItemsToValues: Record<string, TValue | undefined>;

    if (selectionBehavior.mode === "multiple") {
      if (selectedItem.id in selectedItemsToValues) {
        updatedSelectedItemsToValues = { ...selectedItemsToValues };
        delete updatedSelectedItemsToValues[selectedItem.id];
      } else {
        updatedSelectedItemsToValues = {
          ...selectedItemsToValues,
          [selectedItem.id]: selectedItem.value,
        };
      }
    } else if (selectionBehavior.canToggle) {
      updatedSelectedItemsToValues =
        selectedItem.id in selectedItemsToValues
          ? {}
          : { [selectedItem.id]: selectedItem.value };
    } else {
      updatedSelectedItemsToValues = { [selectedItem.id]: selectedItem.value };
    }

    setSelectedItemsToValues(updatedSelectedItemsToValues);

    onSelect?.(selectedItem, selectedItem.id in updatedSelectedItemsToValues);

    if (onSelectionChange) {
      const selectedIds = Object.keys(updatedSelectedItemsToValues);
      onSelectionChange(selectedIds, transformIdsToItems(selectedIds));
    }
  };

  const itemsGap = spaceBetweenItems
    ? spaceBetweenItemsToGap[spaceBetweenItems]
    : isCompact
    ? spaceBetweenItemsToGap.compact
    : spaceBetweenItemsToGap.normal;

  const itemHeight = itemSize
    ? itemSizeToHeight[itemSize]
    : itemSizeToHeight.md;

  const itemsBasedListHeight = maxVisibleItems
    ? maxVisibleItems * (itemHeight + itemsGap) - itemsGap
    : undefined;
  const listHeight = itemsBasedListHeight;

  const listHeightStyles = isHeightFixed
    ? { height: listHeight }
    : { maxHeight: listHeight };

  // Sync selected items with these passed as a prop
  useEffect(() => {
    setSelectedItemsToValues((prevSelectedItemsToValues) => {
      const updatedSelectedItemsToValues: Record<string, TValue | undefined> =
        {};

      Object.keys(prevSelectedItemsToValues).forEach((id) => {
        const item = itemById.get(id);

        if (item) {
          updatedSelectedItemsToValues[id] = item.value;
        }
      });

      return { ...prevSelectedItemsToValues, ...updatedSelectedItemsToValues };
    });
  }, [itemById]);

  // Scrolls to the first selected item when the component is mounted
  useEffect(() => {
    if (shouldScrollToFirstSelectedItem && listRef.current) {
      const firstSelectedItem = listRef.current.querySelector(
        '[data-selected="true"]'
      );

      if (firstSelectedItem) {
        firstSelectedItem.scrollIntoView({ block: "center" });
      }
    }
  }, [shouldScrollToFirstSelectedItem]);

  return (
    <div className={className}>
      {hasSearch && (
        <>
          <SearchInput
            variant="filled"
            color="grey"
            onClear={reset}
            autoFocus={hasSearchAutoFocus}
            {...inputProps}
          />
          {filters && (
            <HorizontalScrollableContainer className="mt-4 flex w-full gap-2 overflow-y-auto">
              {filters.map(({ title, value }) => (
                <button
                  key={value}
                  type="button"
                  className={cx(
                    "font-secondary text-dark-700 leading-2 bg-grey-100 max-w-[200px] cursor-pointer rounded-lg border-none px-4 py-1 text-[13px]",
                    {
                      "bg-primary-500 hover:bg-primary-600 text-white":
                        value === selectedFilter,
                    },
                    { "hover:bg-gray-200": value !== selectedFilter }
                  )}
                  onClick={() => onFilterChange?.(value)}
                >
                  <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                    {title}
                  </span>
                </button>
              ))}
            </HorizontalScrollableContainer>
          )}
          <Divider className={cx(isCompact ? "mb-2 mt-3" : "my-4")} />
        </>
      )}
      {extraTopContent}
      <div
        ref={listRef}
        className="jugl__custom-scrollbar -mr-2 flex flex-col overflow-y-auto pr-2"
        style={{ gap: itemsGap, ...listHeightStyles }}
      >
        {addButton && (
          <button
            type="button"
            className="bg-grey-100 hover:bg-grey-200 flex shrink-0 cursor-pointer items-center justify-between rounded-lg border-none px-3 transition-colors"
            style={{ height: itemHeight }}
            onClick={addButton.onClick}
          >
            <div className="flex items-center gap-2.5">
              {addButton.startIcon}
              <span className="text-dark font-[Roboto] text-sm">
                {addButton.label}
              </span>
            </div>
            <AddIcon />
          </button>
        )}
        {isEmpty && !isLoading && (
          <EmptyState
            customMessage={
              hasSearchQuery ? noSearchResultsMessage : emptyStateMessage
            }
          />
        )}
        {loading === "skeleton" ? (
          <div
            className="flex animate-pulse flex-col"
            style={{ gap: itemsGap }}
          >
            {Array.from({ length: maxVisibleItems || 8 }).map(
              (_, index, array) => (
                <div
                  key={+index}
                  className="bg-grey-200 w-full rounded-lg"
                  style={{
                    height: itemHeight,
                    opacity: (1 / array.length) * (array.length - index),
                  }}
                />
              )
            )}
          </div>
        ) : (
          <>
            {visibleItems.map((item, index) => {
              const isSelected = isItemSelected(item.id);

              const isNotInteractive =
                (isSelected &&
                  selectionBehavior.mode === "single" &&
                  !selectionBehavior.canToggle) ||
                selectionBehavior.mode === "readonly";

              const itemProps: ListBoxItemComponentProps = {
                label: renderLabel?.(item, index, isSelected),
                isSelected,
                isInteractive: !isNotInteractive,
                isDisabled: item.isDisabled,
                height: itemHeight,
                startSlot: renderStartIcon?.(item, index, isSelected),
                endSlot: renderEndIcon?.(item, index, isSelected),
                secondaryText: renderSecondaryText?.(item, index, isSelected),
                highlightedText: searchQuery,
                metaAttributes: {
                  "data-selected": isSelected.toString(),
                },
                onClick: () => handleSelect(item),
                buttonType,
              };

              if (renderCustomItem) {
                return (
                  <Fragment key={item.id}>
                    {renderCustomItem(item, index, itemProps)}
                  </Fragment>
                );
              }

              return <ListBoxItemComponent key={item.id} {...itemProps} />;
            })}
            <div
              ref={inViewRef}
              className={cx("shrink-0", {
                "h-[1px]": loading !== "bottom-spinner",
              })}
            >
              {loading === "bottom-spinner" && (
                <Lottie
                  style={{ marginTop: itemsGap }}
                  options={{ animationData }}
                  height={32}
                  width={32}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const ListBox = forwardRef(ListBoxInner) as <TValue>(
  props: ListBoxProps<TValue> & { ref?: ForwardedRef<ListBoxHandle<TValue>> }
) => ReturnType<typeof ListBoxInner>;
